import type { AssessmentQuestion } from '@ts-assessment/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useAssessmentTestStore } from '@stores/assessment-test'
import { useKidStore } from '@stores/kid'

import { useCsRoutes } from '@composables/routes/cs-route.hook'
import { useKidRoutes } from '@composables/routes/kid-route.hook'

import amplitudeConf from '@config/amplitude.json'
import { pick } from '@helpers/object'

import { trackEvent } from '@tools/amplitude'
import { getNotifyTypeFromUrl } from '@tools/amplitude/notify-type'

interface AssessmentsCreateApiPayload {
  results: AssessmentQuestion[]
  score: number
  comprehension: boolean
}

interface AssessmentsCreateApiOptions {
  onStart?: () => void
  onSuccess?: (id: number | null) => void
  onError?: (err: any) => void
  onFinish?: () => void
}

export const useAssessmentCreateApi = async (payload: AssessmentsCreateApiPayload, options: AssessmentsCreateApiOptions): Promise<number | null> => {
  const { $axios } = useNuxtApp()
  const route = useRoute()
  const requestName = 'complete_assessment_test'

  const kidStore = useKidStore()
  const assessmentTestStore = useAssessmentTestStore()
  const { isRouteDisciplineEla } = useKidRoutes()
  const kidId = kidStore.getActiveKid?.id || null

  if (!kidId) {
    const message = 'Invalid kid ID.'
    options?.onError?.({ message })

    return null
  } else {
    const url = useCsRoutes('assessments.create', pick({
      '[kid_id]': kidId,
      notify_type: getNotifyTypeFromUrl(route),
    }))

    options?.onStart?.()
    assessmentTestStore.setLoading(true)

    const body = {
      discipline: {
        code: isRouteDisciplineEla.value ? Discipline.ELA : Discipline.Math,
      },
      assessment: {
        data: payload.results,
        score: payload.score,
        comprehension_check_up: payload.comprehension,
      },
    }

    try {
      const { data } = await $axios.post(url!, body)
      assessmentTestStore.setLoading(false)
      options?.onSuccess?.(data.data.id)

      trackEvent(amplitudeConf.events.requestSuccess, {
        name: requestName,
        value: data.data,
      })

      return data.data.id
    } catch (err: any) {
      console.error(err)
      options?.onError?.(err)

      trackEvent(amplitudeConf.events.requestError, {
        name: requestName,
        code: err.code,
        description: err.message,
      })

      return null
    } finally {
      options?.onFinish?.()
    }
  }
}
